import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { filmCategories, films } from "@/mocks/films";
import { libraryTags } from "@/mocks/content";

interface FilmLibraryProps {
  category: string;
  onCategoryChange: (key: string) => void;
}

export default function FilmLibrary({ category, onCategoryChange }: FilmLibraryProps) {
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return films.filter((film) => {
      const matchCategory =
        category === "all"
          ? true
          : category === "高分电影"
            ? film.score >= 8.5
            : film.category === category;
      if (!matchCategory) return false;

      if (activeTag && !film.tags.includes(activeTag)) return false;

      if (!q) return true;
      return (
        film.title.toLowerCase().includes(q) ||
        film.summary.toLowerCase().includes(q) ||
        film.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [category, query, activeTag]);

  return (
    <section id="library" className="py-16 md:py-24 bg-background-100/60">
      <div className="px-4 md:px-8 max-w-[1280px] mx-auto">
        <Reveal>
          <SectionHeading
            kicker="Library"
            title="片库检索：按板块和关键词挑片"
            desc="下方片单覆盖伦理片、韩剧、短剧与电影四类共 30 部示范条目。切换板块、点选标签或输入关键词，结果会实时收敛，全部只作用于本页已有内容。"
          />
        </Reveal>

        <Reveal>
          <div className="mt-8 md:mt-10 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex flex-wrap gap-2">
              {filmCategories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => onCategoryChange(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full font-label text-sm transition-colors cursor-pointer ${
                    category === cat.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300"
                  }`}
                  title={cat.desc}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="lg:ml-auto relative w-full lg:w-72">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
                aria-hidden="true"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索片名、标签或关键词"
                aria-label="搜索片名、标签或关键词"
                className="w-full pl-9 pr-9 py-2.5 rounded-md bg-background-50 border border-background-200 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="清除搜索"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-800 cursor-pointer"
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs text-foreground-500 mr-1">热门标签</span>
            {libraryTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                className={`font-label text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer ${
                  activeTag === tag
                    ? "bg-accent-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-6 flex items-center justify-between">
          <p className="font-label text-sm text-foreground-600">
            共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部符合条件
          </p>
          {(query || activeTag) ? (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActiveTag(null);
              }}
              className="font-label text-xs text-foreground-500 hover:text-primary-600 cursor-pointer"
            >
              重置筛选
            </button>
          ) : null}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-8 py-16 text-center border border-dashed border-background-300 rounded-xl bg-background-50">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true" />
            <p className="font-label text-sm text-foreground-600 mt-3">
              没有找到符合条件的影片，换个关键词或板块试试。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-6">
            {filtered.map((film, i) => (
              <Reveal key={film.id} delay={(i % 6) * 50}>
                <article
                  className="h-full bg-background-50 border border-background-200 rounded-xl p-5 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-heading text-base font-bold text-foreground-950 truncate">
                        {film.title}
                      </h3>
                      <p className="font-label text-xs text-foreground-500 mt-1">
                        {film.year} · {film.region} · {film.episodes}
                      </p>
                    </div>
                    <span className="shrink-0 font-heading text-lg font-bold text-accent-700">
                      {film.score.toFixed(1)}
                    </span>
                  </div>

                  <p className="text-sm leading-relaxed text-foreground-600 mt-3 line-clamp-3">
                    {film.summary}
                  </p>

                  <div className="flex flex-wrap items-center gap-1.5 mt-4">
                    <span className="font-label text-[11px] px-2 py-0.5 rounded-md bg-primary-100 text-primary-700">
                      {film.category}
                    </span>
                    {film.tags.map((tag) => (
                      <span
                        key={tag}
                        className="font-label text-[11px] px-2 py-0.5 rounded-md bg-background-100 text-foreground-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {film.badge ? (
                    <p className="mt-3 inline-flex items-center gap-1 font-label text-[11px] text-primary-600">
                      <i className="ri-bookmark-line" aria-hidden="true" />
                      {film.badge}
                    </p>
                  ) : null}
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}